import { GetFiatInstanceDetailsData } from "../services/models/PrimeFiat";

export type StatusInfo = {
  title: string;
  description: string;
  statusKey: string;
};

export const shouldShowCadForm = (data?: GetFiatInstanceDetailsData) => {
  if (!data) return false;
  const eventId = data.eventId;
  if ([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(eventId)) return false;
  if (data.kycRequired !== 1) return true;
  if (data.kycStatus === null) return true;
  return false;
};

export const shouldShowEurForm = (data?: GetFiatInstanceDetailsData) => {
  if (!data) return false;
  const eventId = data.eventId;
  if ([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].includes(eventId))
    return false;
  if (data.kycRequired !== 1) return true;
  if (data.kycStatus === null) return true;
  return false;
};

export const shouldShowCadStatus = (data?: GetFiatInstanceDetailsData) => {
  if (!data) return false;
  const eventId = data.eventId;
  if ([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].includes(eventId))
    return true;
  return (
    (eventId === 1 || eventId >= 10) &&
    data.kycRequired === 1 &&
    data.kycStatus !== null &&
    data.kycStatus !== "APPROVED"
  );
};

export const shouldShowEurStatus = (data?: GetFiatInstanceDetailsData) => {
  if (!data) return false;
  const eventId = data.eventId;
  if ([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].includes(eventId))
    return true;
  if (data.isBuying === 0 && eventId === 2) return true;
  return (
    (eventId === 1 || eventId >= 10) &&
    data.kycRequired === 1 &&
    data.kycStatus !== null &&
    data.kycStatus !== "APPROVED"
  );
};

export const shouldShowForm = (
  region: string | undefined,
  data?: GetFiatInstanceDetailsData,
) => (region === "cad" ? shouldShowCadForm(data) : shouldShowEurForm(data));

export const shouldShowStatus = (
  region: string | undefined,
  data?: GetFiatInstanceDetailsData,
) => (region === "cad" ? shouldShowCadStatus(data) : shouldShowEurStatus(data));

export const getCadPaymentRedirect = (
  region: string | undefined,
  data?: GetFiatInstanceDetailsData,
) => {
  if (region !== "cad" || !data) return null;

  const eventId = Number(data.eventId);
  if (eventId === 2 && data.providerPaymentUrl) return data.providerPaymentUrl;
  return null;
};

const getKycStatusInfo = (kycStatus?: string | null): StatusInfo => {
  if (kycStatus === "APPROVED") {
    return {
      title: "KYC Approved",
      description: "Your identity has been successfully verified.",
      statusKey: "completed",
    };
  }
  if (
    kycStatus === "FAILED" ||
    kycStatus === "DECLINED" ||
    kycStatus === "EXPIRED" ||
    kycStatus === "ABANDONED"
  ) {
    return {
      title: `KYC Verification ${kycStatus}`,
      description:
        "Your verification session expired. Please restart the KYC process.",
      statusKey: "failed",
    };
  }
  if (kycStatus === "IN PROGRESS") {
    return {
      title: "KYC in Progress",
      description:
        "Your verification is underway. Complete the remaining steps to proceed.",
      statusKey: "processing",
    };
  }
  if (kycStatus === "IN REVIEW") {
    return {
      title: "KYC Under Review",
      description: "Your KYC is being reviewed. This may take a few moments.",
      statusKey: "processing",
    };
  }
  return {
    title: "KYC Not Initiated",
    description: "You have not started identity verification yet.",
    statusKey: "pending",
  };
};

export const getCadStatusInfo = (
  data?: GetFiatInstanceDetailsData,
): StatusInfo => {
  switch (data?.eventId) {
    case 2:
      return {
        title: "Initiated",
        description:
          "An Interac e-Transfer payment email has been sent to your inbox. Please check your email and complete the payment.",
        statusKey: "initiated",
      };
    case 3:
      return {
        title: "Payment Pending",
        description:
          "Your Interac e-Transfer is being processed. This may take a few moments.",
        statusKey: "processing",
      };
    case 4:
      return {
        title: "Payment Processing",
        description:
          "Your payment has been fulfilled. We are preparing the next steps for your order.",
        statusKey: "processing",
      };
    case 5:
      return {
        title: "Payment Completed",
        description: "Your payment was processed successfully.",
        statusKey: "completed",
      };
    case 6:
      return {
        title: "Settlement Pending",
        description:
          "Your payment is awaiting settlement. This may take a few moments.",
        statusKey: "processing",
      };
    case 7:
      return {
        title: "Settlement Completed",
        description: "Settlement has been completed successfully.",
        statusKey: "completed",
      };
    case 8:
      return {
        title: "Payment Failed",
        description: "We could not process this payment. Please try again.",
        statusKey: "failed",
      };
    case 9:
      return {
        title: "Expired",
        description:
          "This order has expired or was cancelled. Please create a new order to continue.",
        statusKey: "expired",
      };
    case 10:
      return {
        title: "Refund Initiated",
        description:
          "A refund has been initiated. The transfer is being returned.",
        statusKey: "refund processing",
      };
    case 11:
      return {
        title: "Refund Completed",
        description:
          "The transfer has been refunded and returned to the sender.",
        statusKey: "refund success",
      };
    case 12:
      return {
        title: "KYC Failed",
        description:
          "We could not complete this transaction due to a KYC failure. Please verify your details and try again.",
        statusKey: "failed",
      };
    case 13:
      return {
        title: "On Hold",
        description:
          "An error has occurred during the external settlement process. The case will be manually reviewed for resolution. If resolved settlement will reinitiate else refund will be initiated.",
        statusKey: "pending",
      };
    case 14:
      return {
        title: "Awaiting Crypto Deposit",
        description:
          "Please deposit the crypto to the wallet address provided.",
        statusKey: "pending",
      };
    default:
      return getKycStatusInfo(data?.kycStatus);
  }
};

export const getEurStatusInfo = (
  data?: GetFiatInstanceDetailsData,
): StatusInfo => {
  switch (data?.eventId) {
    case 2:
      return {
        title: "Initiated",
        description:
          "Your order is being created. This may take a few moments.",
        statusKey: "initiated",
      };
    case 3:
      return {
        title: "Payment Pending",
        description:
          "Your order is being created. This may take a few moments.",
        statusKey: "processing",
      };
    case 4:
      return {
        title: "Payment Processing",
        description:
          "Your payment is under process. This may take a few moments.",
        statusKey: "processing",
      };
    case 5:
      return {
        title: "Payment Completed",
        description: "Your payment was processed successfully.",
        statusKey: "completed",
      };
    case 6:
      return {
        title: "Refund Initiated",
        description:
          "We could not process this payment due to a KYC mismatch. The amount is being returned to your account.",
        statusKey: "refund processing",
      };
    case 7:
      return {
        title: "Refund Completed",
        description:
          "We could not process this payment due to a KYC mismatch. Your refund was processed successfully.",
        statusKey: "refund success",
      };
    case 8:
      return {
        title: "Payment Failed",
        description: "We could not process this payment. Please try again.",
        statusKey: "failed",
      };
    case 9:
      return {
        title: "Expired",
        description:
          "This order has expired or was cancelled. Please create a new order to continue.",
        statusKey: "cancelled",
      };
    case 10:
      return {
        title: "KYC Failed",
        description:
          "We could not complete this transaction due to a KYC mismatch. Please verify your details and try again.",
        statusKey: "failed",
      };
    case 11:
      return {
        title: "Awaiting Approval",
        description:
          "Your payment is awaiting approval. This may take a few moments.",
        statusKey: "pending",
      };
    case 12:
      return {
        title: "Settlement Pending",
        description:
          "External crypto settlement has been initiated following successful fiat payment.",
        statusKey: "processing",
      };
    case 13:
      return {
        title: "Settlement Completed",
        description:
          "External crypto settlement has been successfully completed.",
        statusKey: "completed",
      };
    case 14:
      return {
        title: "On Hold",
        description:
          "An error has occurred during the external settlement process. The case will be manually reviewed for resolution. If resolved settlement will reinitiate else refund will be initiated.",
        statusKey: "pending",
      };
    default:
      return getKycStatusInfo(data?.kycStatus);
  }
};

export const getStatusInfo = (
  region: string | undefined,
  data?: GetFiatInstanceDetailsData,
) => (region === "cad" ? getCadStatusInfo(data) : getEurStatusInfo(data));
